/**
 * Quadrant Map Component
 * 
 * Displays a visual map of the 5 quadrants and allows interactive selection
 * for clearing operations.
 */

import { ZONE, WOOD, STONE, IRON, FOOD, LAKE, OPEN } from '../core/Constants.js';
import { isRealResource } from '../core/Constants.js';

export class QuadrantMap {
    constructor(containerElement, gameEngine) {
        this.container = containerElement;
        this.gameEngine = gameEngine;
        this.selectedQuadrant = null;
        this.selectedAction = null;
        this.lastCalculationAction = null;
        
        // Callbacks
        this.onQuadrantAction = null;
        this.onMapAction = null;
        
        this.render();
    }
    
    /**
     * Render the quadrant map
     */
    render() {
        // Create a small visual representation of the 21x21 map
        // Scale it down significantly for display
        const cellSize = 3; // pixels per cell
        const mapWidth = 21 * cellSize;
        const mapHeight = 21 * cellSize;
        
        // Create SVG for the map
        let svgContent = `<svg width="${mapWidth}" height="${mapHeight}" viewBox="0 0 ${mapWidth} ${mapHeight}" class="quadrant-map-svg">`;
        
        // Color mapping for quadrants
        const quadrantColors = {
            0: '#333',      // Non-buildable (walls)
            1: '#4a90e2',  // Quadrant 1 - Blue
            2: '#50c878',  // Quadrant 2 - Green
            3: '#ff6b6b',  // Quadrant 3 - Red (center)
            4: '#f39c12',  // Quadrant 4 - Orange
            5: '#9b59b6'   // Quadrant 5 - Purple
        };
        
        // Draw each cell
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                const quadrant = ZONE[i][j];
                const color = quadrantColors[quadrant] || '#333';
                const x = j * cellSize;
                const y = i * cellSize;
                
                svgContent += `<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" fill="${color}" stroke="#000" stroke-width="0.2" class="quadrant-cell" data-i="${i}" data-j="${j}" data-quadrant="${quadrant}"/>`;
            }
        }
        
        svgContent += '</svg>';
        
        this.container.innerHTML = `
            <div class="quadrant-map-container">
                <div class="quadrant-map-header">
                    <h4>Clear Operations</h4>
                </div>
                <div class="quadrant-map-visual">
                    ${svgContent}
                    <div class="quadrant-map-legend">
                        <div class="legend-item">
                            <span class="legend-color" style="background: #4a90e2;"></span>
                            <span>Q1</span>
                        </div>
                        <div class="legend-item">
                            <span class="legend-color" style="background: #50c878;"></span>
                            <span>Q2</span>
                        </div>
                        <div class="legend-item">
                            <span class="legend-color" style="background: #ff6b6b;"></span>
                            <span>Q3</span>
                        </div>
                        <div class="legend-item">
                            <span class="legend-color" style="background: #f39c12;"></span>
                            <span>Q4</span>
                        </div>
                        <div class="legend-item">
                            <span class="legend-color" style="background: #9b59b6;"></span>
                            <span>Q5</span>
                        </div>
                    </div>
                </div>
                <div class="quadrant-map-controls">
                    <div class="quadrant-selection">
                        <div class="selection-label">Select Area:</div>
                        <div class="quadrant-buttons">
                            <button class="quadrant-btn" data-quadrant="1" title="Quadrant 1">Q1</button>
                            <button class="quadrant-btn" data-quadrant="2" title="Quadrant 2">Q2</button>
                            <button class="quadrant-btn" data-quadrant="3" title="Quadrant 3">Q3</button>
                            <button class="quadrant-btn" data-quadrant="4" title="Quadrant 4">Q4</button>
                            <button class="quadrant-btn" data-quadrant="5" title="Quadrant 5">Q5</button>
                            <button class="quadrant-btn map-btn" data-quadrant="map" title="Entire Map">Map</button>
                        </div>
                    </div>
                    <div class="action-selection" id="action-selection" style="display: none;">
                        <div class="selection-label">Clear:</div>
                        <div class="action-buttons">
                            <button class="action-btn" data-action="resources">Resources</button>
                            <button class="action-btn" data-action="buildings">Buildings</button>
                            <button class="action-btn" data-action="non-lakes">Non-Lakes</button>
                            <button class="action-btn" data-action="cabins">Cabins</button>
                            <button class="action-btn" data-action="lakes">Lakes</button>
                        </div>
                    </div>
                    <div class="action-calculation" id="action-calculation" style="display: none;">
                        <div class="calculation-note"></div>
                    </div>
                </div>
            </div>
        `;
        
        // Setup event listeners
        this.setupEventListeners();
    }
    
    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Quadrant button clicks
        document.querySelectorAll('.quadrant-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const quadrant = btn.getAttribute('data-quadrant');
                this.selectQuadrant(quadrant);
            });
        });
        
        // Action button clicks
        document.querySelectorAll('.action-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const action = btn.getAttribute('data-action');
                this.executeAction(action);
            });
            
            // Show calculation on hover for preview (but keep all calculations visible by default)
            btn.addEventListener('mouseenter', (e) => {
                const action = btn.getAttribute('data-action');
                if (action === 'resources' || action === 'non-lakes' || action === 'lakes') {
                    this.updateCalculation(action);
                }
            });
            
            // On mouse leave, show all calculations again
            btn.addEventListener('mouseleave', (e) => {
                const action = btn.getAttribute('data-action');
                if (action === 'resources' || action === 'non-lakes' || action === 'lakes') {
                    // Return to showing all calculations
                    this.updateAllResourceCalculations();
                }
            });
        });
        
        // SVG cell clicks (for visual selection)
        const svg = this.container.querySelector('.quadrant-map-svg');
        if (svg) {
            svg.addEventListener('click', (e) => {
                const cell = e.target.closest('.quadrant-cell');
                if (cell) {
                    const quadrant = parseInt(cell.getAttribute('data-quadrant'));
                    if (quadrant > 0) {
                        this.selectQuadrant(quadrant.toString());
                    }
                }
            });
        }
    }
    
    /**
     * Select a quadrant
     */
    selectQuadrant(quadrant) {
        // If quadrant changed, hide calculation
        if (this.selectedQuadrant !== quadrant) {
            const calculation = this.container.querySelector('#action-calculation');
            if (calculation) {
                calculation.style.display = 'none';
            }
            this.lastCalculationAction = null;
        }
        
        // Update button states
        document.querySelectorAll('.quadrant-btn').forEach(btn => {
            btn.classList.remove('active');
            if (btn.getAttribute('data-quadrant') === quadrant) {
                btn.classList.add('active');
            }
        });
        
        this.selectedQuadrant = quadrant;
        
        // Show action selection
        const actionSelection = this.container.querySelector('#action-selection');
        if (actionSelection) {
            actionSelection.style.display = 'block';
        }
        
        // Show calculation for all resource actions when quadrant is selected
        // This will show calculations for Resources, Non-Lakes, and Lakes
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
            this.updateAllResourceCalculations();
        }, 0);
        
        // Highlight cells in SVG
        this.highlightQuadrant(quadrant);
    }
    
    /**
     * Count resources in selected area
     */
    countResources(action, quadrant) {
        if (!this.gameEngine || !this.gameEngine.map) return 0;
        
        let count = 0;
        
        // Convert quadrant to number if it's not 'map'
        const quadrantNum = quadrant === 'map' ? null : parseInt(quadrant, 10);
        
        for (let i = 1; i < 21; i++) {
            for (let j = 1; j < 21; j++) {
                // Filter by quadrant if specified
                if (quadrantNum !== null && ZONE[i][j] !== quadrantNum) continue;
                
                const tile = this.gameEngine.map[i][j];
                
                if (action === 'resources') {
                    // Count real resources (WOOD, STONE, IRON, LAKE)
                    // Exclude OPEN spaces (empty land) - OPEN and FOOD share the same value (0x4200)
                    // Only count actual resource nodes, not empty buildable spaces
                    if (tile === WOOD || tile === STONE || tile === IRON || tile === LAKE) {
                        count++;
                    }
                } else if (action === 'non-lakes') {
                    // Count resources that are not lakes
                    // Exclude OPEN spaces (empty land)
                    if (tile === WOOD || tile === STONE || tile === IRON) {
                        count++;
                    }
                } else if (action === 'lakes') {
                    // Count only lakes
                    if (tile === LAKE) {
                        count++;
                    }
                }
            }
        }
        
        return count;
    }
    
    /**
     * Update all resource calculations (show all three: Resources, Non-Lakes, Lakes)
     */
    updateAllResourceCalculations() {
        if (!this.selectedQuadrant) {
            const calculation = this.container.querySelector('#action-calculation');
            if (calculation) {
                calculation.style.display = 'none';
            }
            return;
        }
        
        const resourcesCount = this.countResources('resources', this.selectedQuadrant);
        const nonLakesCount = this.countResources('non-lakes', this.selectedQuadrant);
        const lakesCount = this.countResources('lakes', this.selectedQuadrant);
        
        const areaText = this.selectedQuadrant === 'map' ? 'Map' : `Quadrant ${this.selectedQuadrant}`;
        
        const calculation = this.container.querySelector('#action-calculation');
        const note = calculation?.querySelector('.calculation-note');
        
        if (!calculation) {
            console.error('Calculation element not found', { 
                container: this.container, 
                containerId: this.container?.id,
                html: this.container?.innerHTML?.substring(0, 200)
            });
            return;
        }
        
        if (!note) {
            console.error('Calculation note not found', { calculation });
            return;
        }
        
        // Always show the calculation section when a quadrant is selected
        // Even if there are 0 resources, we'll show that information
        calculation.style.display = 'block';
        
        // If no resources, show a message
        if (resourcesCount === 0 && nonLakesCount === 0 && lakesCount === 0) {
            note.innerHTML = `<div class="calculation-title">Clear Operations for ${areaText}:</div><div class="calculation-section"><div class="calculation-section-title">No resources to clear in this area.</div></div>`;
            return;
        }
        
        let html = `<div class="calculation-title">Clear Operations for ${areaText}:</div>`;
        
        // Resources calculation
        if (resourcesCount > 0) {
            const totalHours = resourcesCount;
            const demolishTime = totalHours === 1 ? '1 Hour' : `${totalHours} Hours`;
            const totalCharisma = resourcesCount * 50;
            const totalDenari = resourcesCount * 249;
            
            html += `
                <div class="calculation-section">
                    <div class="calculation-section-title">Resources (${resourcesCount} node${resourcesCount !== 1 ? 's' : ''}):</div>
                    <div class="calculation-item">
                        <span class="calculation-label">Total Demolish Time:</span>
                        <span class="calculation-value">${demolishTime}</span>
                    </div>
                    <div class="calculation-item">
                        <span class="calculation-label">Total Magic Powder Cost:</span>
                        <div class="calculation-cost">
                            <img src="./assets/images/ui/charisma.png" alt="Charisma" style="width: 14px; height: 14px; vertical-align: middle; margin-right: 2px;" onerror="this.style.display='none'">
                            <span>${totalCharisma}</span>
                            <img src="./assets/images/ui/Denari.png" alt="Denari" style="width: 14px; height: 14px; vertical-align: middle; margin-left: 6px; margin-right: 2px;" onerror="this.style.display='none'">
                            <span>${totalDenari}</span>
                        </div>
                    </div>
                </div>
            `;
        }
        
        // Non-Lakes calculation
        if (nonLakesCount > 0) {
            const totalHours = nonLakesCount;
            const demolishTime = totalHours === 1 ? '1 Hour' : `${totalHours} Hours`;
            const totalCharisma = nonLakesCount * 50;
            const totalDenari = nonLakesCount * 249;
            
            html += `
                <div class="calculation-section">
                    <div class="calculation-section-title">Non-Lakes (${nonLakesCount} node${nonLakesCount !== 1 ? 's' : ''}):</div>
                    <div class="calculation-item">
                        <span class="calculation-label">Total Demolish Time:</span>
                        <span class="calculation-value">${demolishTime}</span>
                    </div>
                    <div class="calculation-item">
                        <span class="calculation-label">Total Magic Powder Cost:</span>
                        <div class="calculation-cost">
                            <img src="./assets/images/ui/charisma.png" alt="Charisma" style="width: 14px; height: 14px; vertical-align: middle; margin-right: 2px;" onerror="this.style.display='none'">
                            <span>${totalCharisma}</span>
                            <img src="./assets/images/ui/Denari.png" alt="Denari" style="width: 14px; height: 14px; vertical-align: middle; margin-left: 6px; margin-right: 2px;" onerror="this.style.display='none'">
                            <span>${totalDenari}</span>
                        </div>
                    </div>
                </div>
            `;
        }
        
        // Lakes calculation
        if (lakesCount > 0) {
            const totalHours = lakesCount;
            const demolishTime = totalHours === 1 ? '1 Hour' : `${totalHours} Hours`;
            const totalCharisma = lakesCount * 50;
            const totalDenari = lakesCount * 249;
            
            html += `
                <div class="calculation-section">
                    <div class="calculation-section-title">Lakes (${lakesCount} node${lakesCount !== 1 ? 's' : ''}):</div>
                    <div class="calculation-item">
                        <span class="calculation-label">Total Demolish Time:</span>
                        <span class="calculation-value">${demolishTime}</span>
                    </div>
                    <div class="calculation-item">
                        <span class="calculation-label">Total Magic Powder Cost:</span>
                        <div class="calculation-cost">
                            <img src="./assets/images/ui/charisma.png" alt="Charisma" style="width: 14px; height: 14px; vertical-align: middle; margin-right: 2px;" onerror="this.style.display='none'">
                            <span>${totalCharisma}</span>
                            <img src="./assets/images/ui/Denari.png" alt="Denari" style="width: 14px; height: 14px; vertical-align: middle; margin-left: 6px; margin-right: 2px;" onerror="this.style.display='none'">
                            <span>${totalDenari}</span>
                        </div>
                    </div>
                </div>
            `;
        }
        
        note.innerHTML = html;
    }
    
    /**
     * Update calculation display for a specific action (used for hover preview)
     */
    updateCalculation(action) {
        // Only show for resource-related actions
        if (action !== 'resources' && action !== 'non-lakes' && action !== 'lakes') {
            // If not a resource action, show all calculations instead
            this.updateAllResourceCalculations();
            return;
        }
        
        if (!this.selectedQuadrant) return;
        
        // Store the action so we can keep showing it
        this.lastCalculationAction = action;
        
        const count = this.countResources(action, this.selectedQuadrant);
        
        if (count === 0) {
            // If this specific action has no resources, show all calculations
            this.updateAllResourceCalculations();
            return;
        }
        
        // Calculate totals
        // Demolish Time: 1 Hour per resource node
        const totalHours = count;
        const demolishTime = totalHours === 1 ? '1 Hour' : `${totalHours} Hours`;
        
        // Magic Powder Cost: 50 Charisma + 249 Denari per resource node
        const totalCharisma = count * 50;
        const totalDenari = count * 249;
        
        const areaText = this.selectedQuadrant === 'map' ? 'Map' : `Quadrant ${this.selectedQuadrant}`;
        
        const calculation = this.container.querySelector('#action-calculation');
        const note = calculation?.querySelector('.calculation-note');
        
        if (calculation && note) {
            calculation.style.display = 'block';
            const actionName = action === 'resources' ? 'Resources' : action === 'non-lakes' ? 'Non-Lakes' : 'Lakes';
            note.innerHTML = `
                <div class="calculation-title">Clearing ${actionName} in ${areaText}:</div>
                <div class="calculation-section">
                    <div class="calculation-section-title">${count} resource node${count !== 1 ? 's' : ''}</div>
                    <div class="calculation-item">
                        <span class="calculation-label">Total Demolish Time:</span>
                        <span class="calculation-value">${demolishTime}</span>
                    </div>
                    <div class="calculation-item">
                        <span class="calculation-label">Total Magic Powder Cost:</span>
                        <div class="calculation-cost">
                            <img src="./assets/images/ui/charisma.png" alt="Charisma" style="width: 14px; height: 14px; vertical-align: middle; margin-right: 2px;" onerror="this.style.display='none'">
                            <span>${totalCharisma}</span>
                            <img src="./assets/images/ui/Denari.png" alt="Denari" style="width: 14px; height: 14px; vertical-align: middle; margin-left: 6px; margin-right: 2px;" onerror="this.style.display='none'">
                            <span>${totalDenari}</span>
                        </div>
                    </div>
                </div>
            `;
        }
    }
    
    /**
     * Hide calculation (called when buildings are added)
     */
    hideCalculation() {
        const calculation = this.container.querySelector('#action-calculation');
        if (calculation) {
            calculation.style.display = 'none';
        }
        this.lastCalculationAction = null;
    }
    
    /**
     * Update calculation after clearing (refresh counts)
     */
    updateCalculationAfterClear() {
        if (this.selectedQuadrant) {
            this.updateAllResourceCalculations();
        }
    }
    
    /**
     * Highlight quadrant in SVG
     */
    highlightQuadrant(quadrant) {
        const cells = this.container.querySelectorAll('.quadrant-cell');
        cells.forEach(cell => {
            const cellQuadrant = parseInt(cell.getAttribute('data-quadrant'));
            if (quadrant === 'map' || cellQuadrant === parseInt(quadrant)) {
                cell.style.opacity = '1';
                cell.style.stroke = '#fff';
                cell.style.strokeWidth = '0.5';
            } else {
                cell.style.opacity = '0.3';
                cell.style.stroke = '#000';
                cell.style.strokeWidth = '0.2';
            }
        });
    }
    
    /**
     * Execute an action
     */
    executeAction(action) {
        if (!this.selectedQuadrant) return;
        
        const currentQuadrant = this.selectedQuadrant; // Store before potential reset
        
        if (this.selectedQuadrant === 'map') {
            // Map-wide action
            if (this.onMapAction) {
                this.onMapAction(action);
            }
        } else {
            // Quadrant-specific action
            if (this.onQuadrantAction) {
                this.onQuadrantAction(parseInt(this.selectedQuadrant), action);
            }
        }
        
        // For clear operations, update calculation instead of resetting
        // Only reset for non-clear actions (if any)
        if (action === 'resources' || action === 'buildings' || action === 'non-lakes' || 
            action === 'cabins' || action === 'lakes') {
            // Keep selection and update calculation
            this.selectedQuadrant = currentQuadrant;
            // Update calculation after a short delay to allow map to update
            setTimeout(() => {
                this.updateCalculationAfterClear();
            }, 100);
        } else {
            // Reset selection for other actions
            this.resetSelection();
        }
    }
    
    /**
     * Reset selection
     */
    resetSelection() {
        this.selectedQuadrant = null;
        this.selectedAction = null;
        
        // Reset button states
        document.querySelectorAll('.quadrant-btn').forEach(btn => {
            btn.classList.remove('active');
        });
        
        // Hide action selection
        const actionSelection = this.container.querySelector('#action-selection');
        if (actionSelection) {
            actionSelection.style.display = 'none';
        }
        
        // Hide calculation
        const calculation = this.container.querySelector('#action-calculation');
        if (calculation) {
            calculation.style.display = 'none';
        }
        
        // Reset SVG highlights
        const cells = this.container.querySelectorAll('.quadrant-cell');
        cells.forEach(cell => {
            cell.style.opacity = '1';
            cell.style.stroke = '#000';
            cell.style.strokeWidth = '0.2';
        });
    }
}

